import { CryptoError } from './errors.js'
import { toBufferSource } from './encoding.js'

/**
 * Ed25519 signatures via WebCrypto — PRD §7.4, §9.5.
 *
 * Key format (the documented transport format for both the app and the service):
 * - public key: raw 32 bytes (RFC 8032 §5.1.5) — base64url without padding in transport;
 * - private key: raw 32-byte seed (the RFC 8032 secret scalar seed) — the caller
 *   keeps it in platform secure storage and never logs it;
 * - signature: raw 64 bytes (R ‖ S) — base64url without padding in transport.
 *
 * DER is never part of the contract: it is materialized only at the WebCrypto
 * boundary (PKCS#8 for signing, SPKI for verification) so the raw format stays
 * portable. `key_id` is bound to the public key by the caller (the service key
 * registry / key metadata record); this module only carries it and refuses an
 * empty id. Key material is never logged and never returned in error messages.
 */

export const ED25519_PROFILE_V1 = {
  id: 'signbento-ed25519-v1',
  algorithm: 'Ed25519',
  public_key_bytes: 32,
  private_key_bytes: 32,
  signature_bytes: 64,
} as const

export interface SigningKeyPair {
  readonly key_id: string
  readonly public_key: Uint8Array
  readonly private_key: Uint8Array
}

const MAX_KEY_ID_LENGTH = 128

/** RFC 8410 PKCS#8 prefix for an Ed25519 private key followed by the 32-byte seed. */
const PKCS8_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
])

/** RFC 8410 SPKI prefix for an Ed25519 public key followed by the 32 raw bytes. */
const SPKI_PREFIX = Uint8Array.from([
  0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00,
])

function invalidKey(message: string): CryptoError {
  return new CryptoError('CRYPTO_INVALID_KEY', message)
}

function assertKeyId(key_id: string): void {
  if (typeof key_id !== 'string' || key_id.length === 0 || key_id.length > MAX_KEY_ID_LENGTH) {
    throw invalidKey('key_id must be a non-empty string of at most 128 characters')
  }
}

function wrapDer(prefix: Uint8Array, raw: Uint8Array, expected: number, what: string): Uint8Array {
  if (raw.byteLength !== expected) {
    throw invalidKey(`Ed25519 ${what} must be exactly ${expected} raw bytes`)
  }
  const der = new Uint8Array(prefix.byteLength + expected)
  der.set(prefix, 0)
  der.set(raw, prefix.byteLength)
  return der
}

function unwrapDer(
  prefix: Uint8Array,
  der: Uint8Array,
  expected: number,
  what: string,
): Uint8Array {
  if (der.byteLength !== prefix.byteLength + expected) {
    throw invalidKey(`${what} has an unexpected DER length`)
  }
  for (let index = 0; index < prefix.byteLength; index += 1) {
    if (der[index] !== prefix[index]) throw invalidKey(`${what} is not a plain Ed25519 key`)
  }
  return der.slice(prefix.byteLength)
}

async function importPrivateKey(private_key: Uint8Array): Promise<CryptoKey> {
  const der = wrapDer(
    PKCS8_PREFIX,
    private_key,
    ED25519_PROFILE_V1.private_key_bytes,
    'private key',
  )
  return globalThis.crypto.subtle.importKey(
    'pkcs8',
    toBufferSource(der),
    { name: 'Ed25519' },
    false,
    ['sign'],
  )
}

async function importPublicKey(public_key: Uint8Array): Promise<CryptoKey> {
  const der = wrapDer(SPKI_PREFIX, public_key, ED25519_PROFILE_V1.public_key_bytes, 'public key')
  return globalThis.crypto.subtle.importKey(
    'spki',
    toBufferSource(der),
    { name: 'Ed25519' },
    false,
    ['verify'],
  )
}

/** Generate a fresh Ed25519 key pair; `key_id` is the caller's stable public identifier. */
export async function generateEd25519KeyPair(key_id: string): Promise<SigningKeyPair> {
  assertKeyId(key_id)
  const generated = await globalThis.crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
    'sign',
    'verify',
  ])
  if (!('privateKey' in generated)) {
    throw invalidKey('Ed25519 key generation did not return a key pair')
  }
  // `Ed25519` has no dedicated WebCrypto overload in the Node typings, so the
  // call resolves to `CryptoKeyPair | CryptoKey`; the guard above establishes
  // the pair shape.
  const pair = generated as unknown as {
    readonly privateKey: CryptoKey
    readonly publicKey: CryptoKey
  }
  const pkcs8 = new Uint8Array(await globalThis.crypto.subtle.exportKey('pkcs8', pair.privateKey))
  const public_key = new Uint8Array(await globalThis.crypto.subtle.exportKey('raw', pair.publicKey))
  const private_key = unwrapDer(
    PKCS8_PREFIX,
    pkcs8,
    ED25519_PROFILE_V1.private_key_bytes,
    'generated private key',
  )
  if (public_key.byteLength !== ED25519_PROFILE_V1.public_key_bytes) {
    throw invalidKey('generated public key is not 32 raw bytes')
  }
  return { key_id, public_key, private_key }
}

/** Sign exact message bytes; the caller canonicalizes (e.g. JCS) before signing. */
export async function signEd25519(
  private_key: Uint8Array,
  message: Uint8Array,
): Promise<Uint8Array> {
  const key = await importPrivateKey(private_key)
  return new Uint8Array(
    await globalThis.crypto.subtle.sign({ name: 'Ed25519' }, key, toBufferSource(message)),
  )
}

/**
 * Verify a signature; fail-closed predicate — malformed lengths and WebCrypto
 * rejections return `false` rather than throwing, so a verifier never confuses
 * a bad signature with a crashed stage.
 */
export async function verifyEd25519(
  public_key: Uint8Array,
  message: Uint8Array,
  signature: Uint8Array,
): Promise<boolean> {
  if (
    public_key.byteLength !== ED25519_PROFILE_V1.public_key_bytes ||
    signature.byteLength !== ED25519_PROFILE_V1.signature_bytes
  ) {
    return false
  }
  try {
    const key = await importPublicKey(public_key)
    return await globalThis.crypto.subtle.verify(
      { name: 'Ed25519' },
      key,
      toBufferSource(signature),
      toBufferSource(message),
    )
  } catch {
    return false
  }
}
